import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import Reveal, { SectionHeading } from "@/components/Reveal";

// Tuiles prédécoupées dans les photographies de Daniel — une œuvre par lettre
const CROPS = [
  { tile: "/images/lettre-1.jpg", scale: 1, dy: 0, rot: -1.5 },
  { tile: "/images/lettre-2.jpg", scale: 0.94, dy: 12, rot: 1.5 },
  { tile: "/images/lettre-3.jpg", scale: 1.05, dy: -8, rot: -1 },
  { tile: "/images/lettre-4.jpg", scale: 0.96, dy: 14, rot: 2 },
  { tile: "/images/lettre-5.jpg", scale: 1.02, dy: -6, rot: -2 },
  { tile: "/images/lettre-6.jpg", scale: 0.92, dy: 10, rot: 1 },
];

function ArtLetter({ letter, index }) {
  const crop = CROPS[index % CROPS.length];
  return (
    <motion.span
      initial={{ opacity: 0, y: 60, rotate: crop.rot * 2.5, scale: crop.scale * 0.85 }}
      animate={{ opacity: 1, y: crop.dy, rotate: crop.rot, scale: crop.scale }}
      transition={{ delay: index * 0.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="inline-block leading-none select-none font-serif font-semibold"
      style={{
        backgroundImage: `url(${crop.tile})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        filter: "drop-shadow(0 0 2px rgba(212,175,55,0.7)) drop-shadow(0 18px 40px rgba(0,0,0,0.9))",
         }}
      data-testid={`word-letter-${index}`}
    >
      {letter}
    </motion.span>
  );
}

export default function Creations() {
  const [selectedImage, setSelectedImage] = useState(null);
  const word = "DULUDE";
  const letters = useMemo(() => word.toUpperCase().replace(/[^A-ZÀ-Þ]/g, "").slice(0, 10).split(""), [word]);

  return (
    <main data-testid="creations-page" className="pt-20">
      <section className="max-w-[1700px] mx-auto px-4 lg:px-8 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-2 lg:px-6">
        <SectionHeading number="01" eyebrow="Créations artistiques" title="Composez votre propre mot"
                        subtitle="Chaque lettre est formée de mes photographies — visages, silhouettes et détails détourés dans la typographie." />
        </div>

        <Reveal className="mt-14">
          <div className="flex flex-col items-center gap-4" data-testid="word-composer-controls">
            <a
              href="https://danphotographemariage.gallery.aftershoot.com/collection/les-cr-ations-dulude"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="word-gallery-button"
              className="bg-[#121216] border border-[#D4AF37]/30 hover:border-[#D4AF37]/70 px-6 py-4 text-center font-serif text-xl tracking-[0.3em] uppercase text-[#F8F9FA] transition-colors w-full sm:w-72 block"
            >
              DULUDE
            </a>
          </div>
        </Reveal>

        <div className="relative mt-20 mb-6" data-testid="word-composer-display">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.1),transparent_65%)] pointer-events-none" />
          <div className="relative flex flex-nowrap justify-center items-center gap-[0.02em] text-[clamp(60px,19vw,420px)] min-h-[200px] sm:min-h-[380px] lg:min-h-[520px]">
            {letters.map((letter, i) => (
              <ArtLetter key={`${word}-${i}`} letter={letter} index={i} />
            ))}
            {letters.length === 0 && (
              <p className="text-slate-500 font-mono text-xs tracking-[0.3em] uppercase py-16">Écrivez un mot pour commencer</p>
            )}
          </div>
          <div className="gold-line w-40 mx-auto mt-10" />
          <p className="font-script text-3xl text-[#D4AF37] text-center mt-6">Daniel Dulude</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
  <img src="/images/La création par Dan.jpg" alt="Création de Daniel Dulude" className="w-full aspect-square object-cover cursor-pointer" onClick={() => setSelectedImage("/images/La création par Dan.jpg")} />
 <img src="/images/PAPILLONS CREATION DAN.jpg" alt="Création de Daniel Dulude" className="w-full aspect-square object-cover cursor-pointer" onClick={() => setSelectedImage("/images/PAPILLONS CREATION DAN.jpg")} />
<img src="/images/pAPILLONS WOW.jpg" alt="Création de Daniel Dulude" className="w-full aspect-square object-cover cursor-pointer" onClick={() => setSelectedImage("/images/pAPILLONS WOW.jpg")} />
 <img src="/images/soleil femme paysage.jpg" alt="Création de Daniel Dulude" className="w-full aspect-square object-cover cursor-pointer" onClick={() => setSelectedImage("/images/soleil femme paysage.jpg")} />
  <img src="/images/Sylvain calque S.png" alt="Création de Daniel Dulude" className="w-full aspect-square object-cover cursor-pointer" onClick={() => setSelectedImage("/images/Sylvain calque S.png")} />
  <img src="/images/Taina avec Dance.jpg" alt="Création de Daniel Dulude" className="w-full aspect-square object-cover cursor-pointer" onClick={() => setSelectedImage("/images/Taina avec Dance.jpg")} />
</div>
        </div>
      </section>
        {selectedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6"
            onClick={() => setSelectedImage(null)}
          >
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white text-4xl hover:text-[#D4AF37] transition-colors"
              aria-label="Fermer"
            >
              ×
            </button>

            <img
              src={selectedImage}
              alt="Création de Daniel Dulude"
              className="max-w-[95vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
<section className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24" data-testid="collection-section">
 <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <SectionHeading number="02" eyebrow="La collection" title="Les créations DULUDE"
                          subtitle="Découvrez mes créations photographiques et mes compositions artistiques." />
          <Reveal delay={0.3}>
            <a
              href="https://danphotographemariage.gallery.aftershoot.com/collection/les-cr-ations-dulude"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discover-creations-button"
              className="btn-gold mt-12 inline-flex"
            >
              Découvrir les créations
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
